import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import { CreditTransactionDto } from 'src/generated/services/TFinancialApi';
import { CreditTransactionFilters, useCreditTransactionList, useCreditTransactionsMetadata } from 'src/use-cases/settings/useCreditTransactions';
import { useBilledEntities } from 'src/use-cases/settings/useBilledEntities';
import { useEnhancedTable } from 'src/presentations/hooks/useEnhancedTable';
import { SettingsBaseTable } from 'src/presentations/components/molecules/settings/table/BaseTable';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import IconDollar from 'src/assets/images/icons/dollar-circle.svg';
import IconDollarYellow from 'src/assets/images/icons/dollar-circle-yellow.svg';
import IconReceipt from 'src/assets/images/icons/receipt-landing.svg';
import IconAddCircle from 'src/assets/images/icons/add-circle.svg';
import IconEdit from 'src/assets/images/icons/arrow-swap.svg';
import { beautifyEnum } from 'src/utils/utility';
import { formatDateString, newFormatDateString } from 'src/utils/date';
import { useFormContext } from 'react-hook-form';
import { CircularProgress } from '@mui/material';
import { SettingsColumnHeader } from '../SettingsColumnHeader';

interface CreditTransactionsTableProps {
  searchQuery?: string;
}

// Transaction type configuration
const TRANSACTION_TYPE_CONFIG = {
  ADD_CREDIT: {
    label: 'Credit Added',
    icon: IconAddCircle,
    bgColor: 'bg-green-100',
  },
  AMEND: {
    label: 'Credit Amended',
    icon: IconEdit,
    bgColor: 'bg-blue-100',
  },
  PROCESS_INVOICE: {
    label: 'Invoice Processing',
    icon: IconReceipt,
    bgColor: 'bg-orange-100',
  },
} as const;

// Amount display configuration
const AMOUNT_COLORS = {
  positive: 'text-green-50',
  negative: 'text-red-40',
  zero: '',
} as const;

// Column factory functions
const createFilterableColumn = (
  accessorKey: keyof CreditTransactionDto,
  header: string,
  metadataKey: string,
  metadataData: any,
  handleTableSort: (columnId: string, direction: 'asc' | 'desc') => void,
  handleTableFilter: (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => void,
  filters: CreditTransactionFilters,
  options: {
    minWidth?: string;
    width?: string;
    cellRenderer?: (cell: any) => React.ReactNode;
    filterValueFormatter?: (value: string) => string;
  } = {}
): MRT_ColumnDef<CreditTransactionDto> => {
  let filterItems = createFilterItems(metadataData?.data?.[metadataKey]);

  // Apply formatting to filter items if formatter is provided
  // But preserve '(Blanks)' labels for null/dash values by checking the original ID
  if (options.filterValueFormatter && filterItems) {
    filterItems = filterItems.map((item) => {
      // Check the original ID value - if it's null, empty, or represents "no value", keep as '(Blanks)'
      const originalValue = item.id;
      const shouldKeepAsNotSet =
        originalValue === null ||
        originalValue === 'null' ||
        originalValue === '' ||
        originalValue === '0 Shipment' ||
        originalValue === '-' ||
        item.label === '(Blanks)';

      return {
        ...item,
        label: shouldKeepAsNotSet ? '(Blanks)' : options.filterValueFormatter!(item.label),
      };
    });
  }

  // Map accessorKey to the correct filter key (this matches the mapping in handleTableFilter)
  const accessorToFilterKeyMap: Record<string, keyof CreditTransactionFilters> = {
    transactionType: 'transactionTypes',
    billedEntity: 'billedEntities',
    invoiceNumber: 'invoiceNumbers',
    shipmentCount: 'shipmentCounts',
    serviceProvider: 'serviceProviders',
    modalities: 'modalities',
    poNumber: 'poNumbers',
  };

  const filterKey = accessorToFilterKeyMap[accessorKey as string] || (accessorKey as keyof CreditTransactionFilters);
  const filterValue = filters[filterKey];

  // Ensure initialSelectedValues is always an array or undefined (to match TableHeaderProps)
  const initialSelectedValues = Array.isArray(filterValue) ? filterValue : undefined;

  return {
    accessorKey,
    header,
    enableSorting: false,
    muiTableHeadCellProps: {
      sx: {
        ...(options.minWidth && { minWidth: options.minWidth }),
        ...(options.width && { width: options.width }),
      },
    },
    Header: ({ column }) => (
      <SettingsColumnHeader
        column={column}
        filterItems={filterItems}
        currentFilters={initialSelectedValues}
        onSort={handleTableSort}
        onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
      />
    ),
    Cell:
      options.cellRenderer ||
      (({ cell }) => {
        const value = cell.getValue() as string;
        return value || '-';
      }),
  };
};

const createAmountColumn = (
  accessorKey: keyof CreditTransactionDto,
  header: string,
  icon: string,
  altText: string,
  handleTableSort: (columnId: string, direction: 'asc' | 'desc') => void,
  options: { minWidth?: string; width?: string } = {}
): MRT_ColumnDef<CreditTransactionDto> => ({
  accessorKey,
  header,
  Header: ({ column }) => (
    <SettingsColumnHeader column={column} onSort={handleTableSort} customHeader={<img src={icon} alt={altText} className='w-16 h-16' />} />
  ),
  enableSorting: false,
  muiTableHeadCellProps: {
    sx: {
      ...(options.minWidth && { minWidth: options.minWidth }),
      ...(options.width && { width: options.width }),
    },
  },
  Cell: ({ cell }) => {
    const amount = cell.getValue() as number;
    if (amount === undefined || amount === null) return '-';

    const colorClass = amount > 0 ? AMOUNT_COLORS.positive : amount < 0 ? AMOUNT_COLORS.negative : AMOUNT_COLORS.zero;
    const prefix = amount > 0 ? '+' : '';
    const displayValue = amount === 0 ? '0' : `${prefix}${amount.toLocaleString()}`;

    return (
      <div className={`flex items-center gap-4 ${colorClass} font-medium`}>
        <span>{displayValue}</span>
      </div>
    );
  },
});

const createDateColumn = (
  accessorKey: keyof CreditTransactionDto,
  header: string,
  handleTableSort: (columnId: string, direction: 'asc' | 'desc') => void,
  formatter: (date: string) => string,
  options: { minWidth?: string; width?: string } = {}
): MRT_ColumnDef<CreditTransactionDto> => ({
  accessorKey,
  header,
  enableSorting: false,
  muiTableHeadCellProps: {
    sx: {
      ...(options.minWidth && { minWidth: options.minWidth }),
      ...(options.width && { width: options.width }),
    },
  },
  Header: ({ column }) => <SettingsColumnHeader column={column} onSort={handleTableSort} />,
  Cell: ({ cell }) => {
    const date = cell.getValue() as string;
    return date ? formatter(date) : '-';
  },
});

export const CreditTransactionsTable: FC<CreditTransactionsTableProps> = ({ searchQuery }): JSX.Element => {
  // Get form context to access current filter values
  const { watch } = useFormContext();
  const formValues = watch();

  // Fetch billed entities to get ID to code mapping
  const { data: billedEntities } = useBilledEntities();

  // State management
  const [filters, setFilters] = useState<CreditTransactionFilters>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use the enhanced table hook
  const { pagination, sorting, handleSortingChange, onChangePage, onChangePageSize, getTextFieldProps } = useEnhancedTable<CreditTransactionDto>({
    initialSorting: [
      {
        id: 'transactionDatetime',
        desc: true, // Show newest first
      },
    ],
    onSearchQueryChange: searchQuery,
    requiredFields: [],
  });

  // Helper function to find entity code by ID recursively
  const findEntityCodeById = useCallback((entities: any[], targetId: number): string | null => {
    if (!entities) return null;

    for (const entity of entities) {
      if (entity.id === targetId) {
        return entity.code;
      }
      // Check sub-entities recursively
      if (entity.subEntities && entity.subEntities.length > 0) {
        const found = findEntityCodeById(entity.subEntities, targetId);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Get billed entity code from ID
  const getBilledEntityCode = useCallback(
    (billedEntityId: string): string | null => {
      if (!billedEntityId || !billedEntities) return null;

      const entityId = Number(billedEntityId);
      if (isNaN(entityId)) return null;

      return findEntityCodeById(billedEntities, entityId);
    },
    [billedEntities, findEntityCodeById]
  );

  // Process sorting state for API request
  let sortingField = sorting.length > 0 ? sorting[0].id : undefined;
  let sortingDirection: 'ASC' | 'DESC' | undefined;

  if (sorting.length > 0) {
    sortingDirection = sorting[0].desc ? 'DESC' : 'ASC';
  }

  // Fetch credit transactions data
  const { data, isFetching, refetch } = useCreditTransactionList(
    {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sortingColum: sortingField,
      sortingDirection,
    },
    filters
  );

  // Get metadata for filter options with current filters
  const { data: metadataData } = useCreditTransactionsMetadata(filters);

  // Refetch data when filters change
  useEffect(() => {
    if (isInitialLoad) {
      setIsInitialLoad(false);
    } else {
      refetch();
    }
  }, [filters, refetch, isInitialLoad, setIsInitialLoad]);

  // Update filters based on form values
  useEffect(() => {
    const newFilters: CreditTransactionFilters = { ...filters };

    // Add billed entity filter if selected
    if (formValues.billedEntityId) {
      const entityId = Number(formValues.billedEntityId);
      if (!isNaN(entityId)) {
        newFilters.creditBilledEntityId = entityId;
      }
    } else {
      // Remove the filter if no entity is selected
      delete newFilters.creditBilledEntityId;
    }

    // Add transaction date filters if selected
    if (formValues.transactionDate && formValues.transactionDate.length > 0) {
      // Pass the transaction dates directly to the API via the dates field
      newFilters.dates = formValues.transactionDate;
    } else {
      // Remove the dates filter if no dates are selected
      delete newFilters.dates;
    }

    // Only update filters if we have both billedEntityId and transactionDate
    // This prevents the initial API call without dates, allowing auto-selection to complete first
    if (formValues.billedEntityId && formValues.transactionDate && formValues.transactionDate.length > 0) {
      setFilters(newFilters);
    }
  }, [formValues.billedEntityId, formValues.transactionDate]);

  // Memoize credit transactions to avoid recreating on every render
  const creditTransactions = useMemo(() => {
    const content = data?.data?.content || [];
    return content;
  }, [data]);

  const creditTransactionsTotal = data?.data?.totalElements || 0;

  // Update a specific filter field
  const updateFilter = useCallback(
    <K extends keyof CreditTransactionFilters>(key: K, value: CreditTransactionFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setFilters]
  );

  // Handler for table header sorting
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      handleSortingChange([{ id: columnId, desc: direction === 'desc' }]);
    },
    [handleSortingChange]
  );

  // Handler for table filters - extracted to reduce duplication
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => {
      // Only clear the filter if NO values are selected
      // Don't clear when all values are selected - that's a valid filter state
      const shouldClearFilter = !selectedValues.length;

      // Column to filter key mapping
      const filterMapping: Record<string, keyof CreditTransactionFilters> = {
        transactionType: 'transactionTypes',
        billedEntity: 'billedEntities',
        invoiceNumber: 'invoiceNumbers',
        shipmentCount: 'shipmentCounts',
        serviceProvider: 'serviceProviders',
        modalities: 'modalities',
        poNumber: 'poNumbers',
      };

      const filterKey = filterMapping[columnId];
      if (!filterKey) return;

      if (shouldClearFilter) {
        updateFilter(filterKey, undefined);
      } else {
        updateFilter(filterKey, selectedValues);
      }

      // Remove refetchMetadata() call to preserve selected filter values
      // The metadata will be updated when the main data refetches
      refetch();
    },
    [updateFilter, refetch]
  );

  // Define table columns
  const columns = useMemo(
    (): MRT_ColumnDef<CreditTransactionDto>[] => [
      // ID column (hidden)
      {
        accessorKey: 'id',
        header: 'ID',
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: true,
      },

      // Transaction Type column with custom rendering
      {
        accessorKey: 'transactionType',
        header: 'Transaction',
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: { minWidth: '220px' },
        },
        Header: ({ column }) => {
          let filterItems = createFilterItems(metadataData?.data?.transactionTypesCounter);

          // Format transaction type filter items to match cell display
          if (filterItems) {
            filterItems = filterItems.map((item) => ({
              ...item,
              label: TRANSACTION_TYPE_CONFIG[item.id as keyof typeof TRANSACTION_TYPE_CONFIG]?.label || item.label,
            }));
          }

          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.transactionTypes}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        Cell: ({ cell }) => {
          const type = cell.getValue() as keyof typeof TRANSACTION_TYPE_CONFIG;
          const config = TRANSACTION_TYPE_CONFIG[type];

          if (!config) return type || '';

          return (
            <div className='flex items-center gap-8'>
              <div className={`w-16 h-16 rounded-full ${config.bgColor} flex items-center justify-center`}>
                <img src={config.icon} alt='Transaction' />
              </div>
              <span>{config.label}</span>
            </div>
          );
        },
      },

      // Date column
      createDateColumn('transactionDatetime', 'Date', handleTableSort, newFormatDateString, { minWidth: '180px' }),

      // File Name column
      {
        accessorKey: 'fileName',
        header: 'File Name',
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: { minWidth: '250px' },
        },
        Header: ({ column }) => <SettingsColumnHeader column={column} onSort={handleTableSort} />,
        Cell: ({ cell }) => {
          const fileName = cell.getValue() as string;
          return (
            <div
              style={{
                wordBreak: 'break-word',
              }}
            >
              {fileName || '-'}
            </div>
          );
        },
      },

      // Filterable columns
      createFilterableColumn('invoiceNumber', 'Invoice', 'invoiceNumbersCounter', metadataData, handleTableSort, handleTableFilter, filters, {
        minWidth: '180px',
      }),
      createFilterableColumn('shipmentCount', 'Shipments', 'shipmentCountsCounter', metadataData, handleTableSort, handleTableFilter, filters, {
        minWidth: '180px',
        cellRenderer: ({ cell }) => {
          let shipmentCount = cell.getValue() as string;
          if (shipmentCount === '0 Shipment') {
            shipmentCount = '';
          }
          return shipmentCount || '-';
        },
        filterValueFormatter: (value) => (value && value !== '0 Shipment' ? value : '-'),
      }),
      createFilterableColumn('serviceProvider', 'Service Provider', 'serviceProvidersCounter', metadataData, handleTableSort, handleTableFilter, filters, {
        minWidth: '140px',
        cellRenderer: ({ cell }) => {
          const serviceProvider = cell.getValue() as string;
          return beautifyEnum(serviceProvider).toUpperCase() || '-';
        },
        filterValueFormatter: (value) => beautifyEnum(value).toUpperCase(),
      }),
      createFilterableColumn('billedEntity', 'Billed Entity', 'billedEntitiesCounter', metadataData, handleTableSort, handleTableFilter, filters, {
        minWidth: '120px',
        cellRenderer: ({ cell }) => {
          const billedEntity = cell.getValue() as string;
          return beautifyEnum(billedEntity).toUpperCase() || '-';
        },
        filterValueFormatter: (value) => beautifyEnum(value).toUpperCase(),
      }),
      createFilterableColumn('modalities', 'Modality', 'modalitiesCounter', metadataData, handleTableSort, handleTableFilter, filters, {
        width: '100px',
        cellRenderer: ({ cell }) => {
          const modalities = cell.getValue() as string[];
          return modalities && modalities.length > 0 ? modalities.join(', ') : '-';
        },
      }),

      // Amount columns
      createAmountColumn('invoiceCreditAmount', 'Invoice Credits', IconDollar, 'Invoice Credits', handleTableSort, { width: '150px' }),
      createAmountColumn('shipmentCreditAmount', 'Shipment Credits', IconDollarYellow, 'Shipment Credits', handleTableSort, { minWidth: '150px' }),

      // Credit Expiration Date
      createDateColumn('creditExpirationDate', 'Expiry Date', handleTableSort, formatDateString, { minWidth: '140px' }),

      // PO Number / Date - custom column due to complex logic
      {
        accessorKey: 'poNumber',
        header: 'PO Number / Date',
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: { minWidth: '240px' },
        },
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.poNumbersCounter);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.poNumbers}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        Cell: ({ row }) => {
          const poNumber = row.original.poNumber;
          const poDate = row.original.poDate;

          if (!poNumber && !poDate) return '-';

          return (
            <div>
              <span>{`${poNumber}${poNumber && poDate ? ' / ' : ''}${formatDateString(poDate)}`}</span>
            </div>
          );
        },
      },
    ],
    [
      getTextFieldProps,
      handleTableSort,
      handleTableFilter,
      metadataData?.data,
      filters, // Use the entire filters object instead of individual properties
    ]
  );

  const baseTableConfig = createTableConfig<CreditTransactionDto>({
    hiddenColumns: ['id'],
  });

  const table = useMaterialReactTable({
    ...baseTableConfig,
    data: creditTransactions,
    columns,
    state: {
      sorting,
      isLoading: isFetching, // Use isFetching for table loading state
    },
    enableRowActions: false,
    enableEditing: false,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 0,
      },
    },
    onSortingChange: (updatedSorting) => {
      if (typeof updatedSorting === 'function') {
        const newSortingState = updatedSorting(sorting);
        handleSortingChange(newSortingState);
      } else {
        handleSortingChange(updatedSorting);
      }
    },
  });

  return (
    <div>
      {isFetching ? (
        <div className='flex items-center justify-center py-20'>
          <CircularProgress size={40} />
        </div>
      ) : (
        <SettingsBaseTable
          table={table}
          totalItems={creditTransactionsTotal}
          pagination={pagination}
          onChangePage={onChangePage}
          onChangePageSize={onChangePageSize}
        />
      )}
    </div>
  );
};
